import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { UserState, storeUser, signOut } from "helpers";
import { Button, CardHeader, ValidationMessage, Input, Loading } from "components";
import { Link, useLocation } from "react-router-dom";
import { signIn, fetchAuthSession } from "aws-amplify/auth";
import { useAuthContext } from "context";
import { UserCognito, PreferredMFA, ChallengeName } from "helpers";
import { useRegisterDeviceMutation, useUpdateProfileMutation } from "services";
import { t } from "i18next";
import { useTranslation } from "react-i18next";

interface AuthLoginFormI {
  email: string;
  password: string;
}

const initialData: AuthLoginFormI = {
  email: "",
  password: "",
};

interface AuthLoginProps extends Pick<UserState, "setUser"> {}

export const AuthLogin = ({ setUser }: AuthLoginProps) => {
  const [updateProfile] = useUpdateProfileMutation();
  const { t } = useTranslation();
  const { setAuth } = useAuthContext();
  const location = useLocation();
  const emailRegex = /^[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{1,}$/i;
  const [data, setData] = useState<AuthLoginFormI>(initialData);
  const [error, setError] = useState("");
  const [disabled, setDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [hideForgotPassword, setHideForgotPassword] = useState(false);
  const [registerDevice, { isError }] = useRegisterDeviceMutation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const emailFromUrl = searchParams.get("email") || "";
    const passwordFromUrl = searchParams.get("pass") || "";
    setData({
      email: emailFromUrl,
      password: passwordFromUrl,
    });
    setHideForgotPassword(!!emailFromUrl && !!passwordFromUrl);
  }, [location.search]);

  const onChangeHandler = (event: ChangeEvent<HTMLInputElement>): void => {
    const {
      target: { name, value },
    } = event;
    setData((oldObject) => ({ ...oldObject, [name]: value }));
  };

  const storeAdditionalUserData = async (username: string, deviceName: string, deviceKey: any) => {
    localStorage.setItem("username", username);
    localStorage.setItem("deviceName", deviceName);
    if (deviceKey) {
      localStorage.setItem("deviceKey", deviceKey);
      sendDeviceData(username, deviceKey, deviceName);
    } else {
      console.warn("No device_key found in access token payload");
    }
  };

  const sendDeviceData = async (username: string, deviceKey: string, deviceName: string) => {
    try {
      const result = await registerDevice({ username, deviceKey, deviceName }).unwrap();
      console.log("Device registration result:", result);
    } catch (error) {
      console.error("Error registering device:", error);
    }
  };

  const handleSignIn = async (username: string, password: string) => {
    setIsLoading(true);
    try {
      const { isSignedIn, nextStep } = await signIn({
        username,
        password,
      });

      switch (nextStep.signInStep) {
        case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED": {
          const partialUser: UserCognito = {
            username,
            accessToken: "",
            idToken: "",
            attributes: {
              sub: "",
              email: username,
              phone_number: "",
              email_verified: false,
              phone_number_verified: false,
            },
            preferredMFA: "NOMFA",
            challengeName: "NEW_PASSWORD_REQUIRED",
          };
          setUser(partialUser);
          setAuth("change-password");
          return;
        }
        case "CONFIRM_SIGN_IN_WITH_SMS_CODE": {
          const deviceName = navigator.userAgent;
          setAuth("2fa");
          return;
        }
        case "RESET_PASSWORD": {
          const partialUser: UserCognito = {
            username,
            accessToken: "",
            idToken: "",
            attributes: {
              sub: "",
              email: username,
              phone_number: "",
              email_verified: false,
              phone_number_verified: false,
            },
            preferredMFA: "NOMFA",
            challengeName: "RESET_PASSWORD",
          };
          setUser(partialUser);
          setAuth("change-password");
          return;
        }
        case "DONE": {
          if (!isSignedIn) {
            throw new Error("Sign-in completed but isSignedIn is false");
          }
          const session = await fetchAuthSession();
          if (!session.tokens) {
            throw new Error("No authentication tokens available after sign-in");
          }

          const payload = session.tokens.idToken?.payload;
          const accessToken = session.tokens.accessToken;
          const idToken = session.tokens.idToken;

          if (!payload || !accessToken || !idToken) {
            throw new Error("Missing required token data (payload, accessToken, or idToken)");
          }

          const cognitoUsername = payload["cognito:username"];
          const resolvedUsername = typeof cognitoUsername === "string" ? cognitoUsername : username;
          const deviceName = navigator.userAgent;
          const accessTokenPayload = session.tokens?.accessToken.payload;
          const deviceKey = accessTokenPayload?.device_key || "";
          await storeAdditionalUserData(resolvedUsername, deviceName, deviceKey);

          const preferredMFA: PreferredMFA = payload["cognito:preferred_mfa_setting"] === "SMS" ? "SMS_MFA" : "NOMFA";

          const user: UserCognito = {
            username: resolvedUsername,
            accessToken: accessToken.toString(),
            idToken: idToken.toString(),
            attributes: {
              sub: payload.sub as string,
              email: payload.email as string,
              phone_number: (payload.phone_number as string) || "",
              email_verified: payload.email_verified === true,
              phone_number_verified: payload.phone_number_verified === true,
            },
            preferredMFA,
          };

          storeUser(user);
          setUser(user);
          await updateProfile({ logged_in: true });

          if (user.attributes.phone_number && !user.attributes.phone_number_verified) {
            setAuth("verify-phone-number");
          } else {
            setAuth("success");
          }
          return;
        }
        default:
          throw new Error(`Unhandled sign-in step: ${nextStep.signInStep}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitHandler = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setDisabled(true);

    try {
      await handleSignIn(data.email, data.password);
    } catch (err: any) {
      console.error("Sign-in error:", err);
      if (err.name === "UserAlreadyAuthenticatedException") {
        console.log("User already authenticated, signing out and retrying...");
        try {
          await signOut();
          await handleSignIn(data.email, data.password);
        } catch (retryErr: any) {
          console.error("Retry sign-in error:", retryErr);
          setError(retryErr.message || t("auth.loginFailed"));
        }
      } else {
        setError(err.message || t("auth.loginFailed"));
      }
    } finally {
      setDisabled(false);
    }
  };

  useEffect(() => {
    setDisabled(!emailRegex.test(data.email) || !data.password);
    if (error) setError("");
  }, [data]);

  useEffect(() => {
    setAuth("login");
  }, [setAuth]);

  return (
    <>
      <CardHeader title={t("auth.welcomeBack")} />
      <form onSubmit={onSubmitHandler}>
        <div className="space-y-4">
          <Input
            type="email"
            id="email"
            name="email"
            value={data.email}
            placeholder={t("auth.enterEmail")}
            onChange={onChangeHandler}
            error={!!error}
            autoComplete="email"
            disabled={isLoading}
          />
          <Input
            type="password"
            id="password"
            name="password"
            value={data.password}
            placeholder={t("auth.enterPassword")}
            onChange={onChangeHandler}
            error={!!error}
            autoComplete="current-password"
            disabled={isLoading}
          />
        </div>
        <div className="mt-2 flex items-center justify-between">
          {!hideForgotPassword && (
            <Link to="/forgot-password" className="text-primary underline">
              {t("auth.forgotPassword")}
            </Link>
          )}
          <ValidationMessage>{error}</ValidationMessage>
        </div>
        <Button type="submit" className="mx-auto mt-5 px-16 sm:min-w-[200px]" disabled={disabled || isLoading}>
          {isLoading ? <Loading height={20} width={20} /> : t("auth.login")}
        </Button>
      </form>
    </>
  );
};
